import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { reviewsQuery } from '../../lib/tasks-api';
import { cx } from '../../lib/cx';
import type { StaffNavGroup, StaffNavItem } from '../../lib/staff-nav';
import { Icon } from '../Icon';
import { NavGroupHeader } from './nav-group-header';

// Every row in the rail is one of two fixed heights (item: 34px, group header: 24px) separated by a 2px gap, and the
// icon sits at a fixed left offset, so nothing moves between the collapsed and open rail except the labels.
const ROW =
  'flex h-(--mbm-size-rail-item-height) w-full items-center gap-(--mbm-space-3) overflow-hidden rounded-(--mbm-component-radius-rail-item) px-(--mbm-space-3) ' +
  'relative text-label-medium whitespace-nowrap focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)';

/**
 * The blue dot on "Review queue" (Figma A01.3): there is something waiting for you. It reads the queue's own count, so
 * it is true; the queue answers 403 to anyone who does not review, and then there is simply no dot.
 */
function ReviewDot() {
  const { data } = useQuery({
    ...reviewsQuery({ tab: 'waiting', sort: 'client_date' }),
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
  const waiting = data?.counts.waiting ?? 0;
  if (waiting === 0) return null;
  return (
    <span
      role="img"
      aria-label={`${waiting} waiting for your QA`}
      className="absolute top-(--mbm-space-1-5) left-(--mbm-space-7) size-(--mbm-size-rail-dot) rounded-full border-[1.5px] border-surface-card bg-brand-primary"
    />
  );
}

function NavItemRow({
  item,
  collapsed,
  onNavigate,
}: {
  item: StaffNavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const content = (
    <>
      {/* shrink-0: the (invisible) label still has its full width when collapsed and would squash the icon to nothing. */}
      <span className="shrink-0">
        <Icon icon={item.icon} size="lg" />
      </span>
      {item.id === 'review-queue' ? <ReviewDot /> : null}
      <span
        className={cx(
          'flex flex-1 items-center justify-between gap-(--mbm-space-2) transition-opacity duration-(--mbm-duration-fast)',
          collapsed ? 'opacity-0' : 'opacity-100',
        )}
      >
        {item.label}
        {item.soon ? (
          <span className="rounded-full bg-tag-blue-bg px-(--mbm-space-2) text-label-small text-tag-blue-text">
            Soon
          </span>
        ) : null}
      </span>
    </>
  );

  if (!item.to) {
    // TODO: link each as its sprint lands. Shown disabled so the rail matches the design without dead links.
    return (
      <span
        aria-disabled="true"
        title={`${item.label} · coming soon`}
        className={cx(ROW, 'cursor-not-allowed text-text-tertiary')}
      >
        {content}
      </span>
    );
  }
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      activeOptions={{ exact: !item.matchChildren }}
      activeProps={{ className: 'bg-surface-tint text-text-primary', 'aria-current': 'page' }}
      inactiveProps={{ className: 'text-text-secondary hover:bg-surface-muted hover:text-text-primary' }}
      className={ROW}
    >
      {content}
    </Link>
  );
}

export interface StaffNavListProps {
  groups: StaffNavGroup[];
  collapsed: boolean;
  onNavigate?: () => void;
}

/** The staff navigation as one flat column: per group, a NavGroupHeader followed by its items. */
export function StaffNavList({ groups, collapsed, onNavigate }: StaffNavListProps) {
  return (
    <ul className="flex flex-col gap-(--mbm-space-0-5)">
      {groups.map((group, index) => (
        <GroupRows
          key={group.heading ?? index}
          group={group}
          first={index === 0}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      ))}
    </ul>
  );
}

function GroupRows({
  group,
  first,
  collapsed,
  onNavigate,
}: {
  group: StaffNavGroup;
  first: boolean;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  return (
    <>
      <NavGroupHeader label={group.heading ?? ''} collapsed={collapsed} first={first} />
      {group.items.map((item) => (
        <li key={item.id}>
          <NavItemRow item={item} collapsed={collapsed} onNavigate={onNavigate} />
        </li>
      ))}
    </>
  );
}
